import { createContext, useContext, useState, useCallback } from "react";
import type { ReactNode } from "react";
import { getSupabase } from "@/lib/supabase";

export type LoginType = "dealer" | "dealer_admin" | "dealer_user";

interface AuthUser {
  id: string;
  loginType: LoginType;
  dealerId?: string;
  dealerCode: string;
  dealerName: string;
  username?: string;
  fullName?: string;
  role?: string;
  isActive: boolean;
  hasAccounting?: boolean;
  hasEArchive?: boolean;
  hasEInvoice?: boolean;
  hasRestaurant?: boolean;
  hasRestaurantStock?: boolean;
  hasRestaurantBranches?: boolean;
  hasRestaurantReports?: boolean;
  currencies?: string[];
}

interface AuthContextType {
  user: AuthUser | null;
  isAuthenticated: boolean;
  login: (loginType: LoginType, dealerCode: string, identifier: string, password: string) => Promise<{ success: boolean; message: string; dealerName: string }>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

const AUTH_FUNCTION_URL = "https://dimwpgeuqvtzlrlvveme.supabase.co/functions/v1/dealer-auth";

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(() => {
    const stored = sessionStorage.getItem("artpos_user");
    return stored ? JSON.parse(stored) : null;
  });

  const isAuthenticated = user !== null;

  const login = useCallback(async (loginType: LoginType, dealerCode: string, identifier: string, password: string) => {
    if (!dealerCode.trim() || !password.trim()) {
      return { success: false, message: "Tüm alanları doldurunuz.", dealerName: "" };
    }

    try {
      const body: Record<string, string> = {
        loginType,
        dealerCode: dealerCode.trim(),
        password,
      };

      if (loginType === "dealer") {
        body.dealerName = identifier.trim();
        body.username = identifier.trim();
      }

      const response = await fetch(AUTH_FUNCTION_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string}`,
        },
        body: JSON.stringify(body),
      });

      const data = await response.json();

      if (data.success && data.user) {
        const userData: AuthUser = {
          id: data.user.id,
          loginType: data.user.loginType || loginType,
          dealerId: data.user.dealerId,
          dealerCode: data.user.dealerCode,
          dealerName: data.user.dealerName,
          username: data.user.username,
          fullName: data.user.fullName,
          role: data.user.role,
          isActive: data.user.isActive,
          hasAccounting: data.user.hasAccounting,
          hasEArchive: data.user.hasEArchive,
          hasEInvoice: data.user.hasEInvoice,
          hasRestaurant: data.user.hasRestaurant,
          hasRestaurantStock: data.user.hasRestaurantStock,
          hasRestaurantBranches: data.user.hasRestaurantBranches,
          hasRestaurantReports: data.user.hasRestaurantReports,
          currencies: data.user.currencies || ["TRY"],
        };
        setUser(userData);
        sessionStorage.setItem("artpos_user", JSON.stringify(userData));

        // Log login
        try {
          await getSupabase().from("dealer_logs").insert({
            dealer_id: userData.dealerId,
            dealer_user_id: userData.id,
            action: "Giriş",
            details: `${userData.dealerName} · ${userData.fullName || userData.username || loginType}`,
          });
        } catch {
          // Log failure is non-critical
        }

        return { success: true, message: data.message, dealerName: userData.dealerName };
      }

      return { success: false, message: data.message || "Giriş başarısız.", dealerName: "" };
    } catch {
      return { success: false, message: "Bağlantı hatası. Lütfen tekrar deneyin.", dealerName: "" };
    }
  }, []);

  const logout = useCallback(() => {
    // Log logout before clearing user
    const stored = sessionStorage.getItem("artpos_user");
    if (stored) {
      try {
        const u = JSON.parse(stored);
        if (u.dealerId) {
          const supabase = getSupabase();
          supabase.from("dealer_logs").insert({
            dealer_id: u.dealerId,
            dealer_user_id: u.id,
            action: "Çıkış",
            details: `${u.dealerName} · ${u.fullName || u.username || u.loginType}`,
          }).then(() => {}).catch(() => {
            // Log failure is non-critical
          });
        }
      } catch {
        // Log failure is non-critical
      }
    }
    setUser(null);
    sessionStorage.removeItem("artpos_user");
  }, []);

  return (
    <AuthContext.Provider value={{ user, isAuthenticated, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}